import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/modules/user/user";
import { QuoteLayout } from "@/enums/quote-layout";

export type QuoteFilterParams = {
  authorUuid?: string | null;
  searchText?: string | null;
  date?: Date | null;
};

type GroupFilters = {
  authorUuid: string | null;
  searchText: string | null;
  dateFilter: Date | null;
};

/**
 * Quote filter store for managing search/filter state
 * Filters are set in the store and trigger automatic refetch
 * Date filters can be loaded from URL query params (for memory features)
 */
export const useQuoteFilterStore = defineStore("quoteFilter", () => {
  const router = useRouter();
  const userStore = useUserStore();

  /* Layout mode preference (persisted in localStorage) */
  const layoutMode = ref<QuoteLayout>(
    (localStorage.getItem("quoteLayout") as QuoteLayout) ?? QuoteLayout.List,
  );

  function setLayoutMode(mode: QuoteLayout): void {
    layoutMode.value = mode;
    localStorage.setItem("quoteLayout", mode);
  }

  /* Per-group filter storage */
  const filtersByGroup = ref<Record<string, GroupFilters>>({});

  /* Computed - active group ID from groups store */
  const activeGroupId = computed(() => userStore.activeGroup);

  /* Helper to ensure group exists */
  function ensureGroupFilters(groupId: string): GroupFilters {
    if (!filtersByGroup.value[groupId]) {
      filtersByGroup.value[groupId] = {
        authorUuid: null,
        searchText: null,
        dateFilter: null,
      };
    }
    return filtersByGroup.value[groupId]!;
  }

  /* Computed properties for current group's filters */
  const authorUuid = computed({
    get: () =>
      activeGroupId.value
        ? ensureGroupFilters(activeGroupId.value).authorUuid
        : null,
    set: (value) => {
      if (activeGroupId.value) {
        ensureGroupFilters(activeGroupId.value).authorUuid = value;
      }
    },
  });

  const searchText = computed({
    get: () =>
      activeGroupId.value
        ? ensureGroupFilters(activeGroupId.value).searchText
        : null,
    set: (value) => {
      if (activeGroupId.value) {
        ensureGroupFilters(activeGroupId.value).searchText = value;
      }
    },
  });

  const dateFilter = computed({
    get: () =>
      activeGroupId.value
        ? ensureGroupFilters(activeGroupId.value).dateFilter
        : null,
    set: (value) => {
      if (activeGroupId.value) {
        ensureGroupFilters(activeGroupId.value).dateFilter = value;
      }
    },
  });

  /* Computed - check if any filters are active */
  function hasActiveFilters(): boolean {
    return !!(authorUuid.value || searchText.value || dateFilter.value);
  }

  /* Get current filter params for service calls */
  function getFilterParams(): QuoteFilterParams {
    return {
      authorUuid: authorUuid.value,
      searchText: searchText.value,
      date: dateFilter.value,
    };
  }

  /* Update Author Filter */
  function setAuthorFilter(uuid: string | null): void {
    authorUuid.value = uuid;
    // Clear other filters when setting author
    if (uuid) {
      searchText.value = null;
      dateFilter.value = null;
    }
    // Don't update URL - filters are derived from URL on load
  }

  /* Update Search Text */
  function setSearchText(text: string | null): void {
    searchText.value = text;
    // Clear other filters when searching
    if (text) {
      authorUuid.value = null;
      dateFilter.value = null;
    }
    // Don't update URL - filters are derived from URL on load
  }

  /* Update Date Filter */
  function setDateFilter(date: Date | null): void {
    dateFilter.value = date;
    // Clear other filters when setting date
    if (date) {
      authorUuid.value = null;
      searchText.value = null;
    }
    // Don't update URL - filters are derived from URL on load
  }

  /* Clear all filters */
  function clearAllFilters(): void {
    authorUuid.value = null;
    searchText.value = null;
    dateFilter.value = null;
    // Don't update URL - filters are derived from URL on load
  }

  /* Sync date filter from URL query on page load */
  function loadFiltersFromUrl(): void {
    if (!activeGroupId.value) return;

    const route = router.currentRoute.value;
    const filters = ensureGroupFilters(activeGroupId.value);

    // Load date from query params (for memory features)
    if (route.query?.date && typeof route.query.date === "string") {
      const dateObj = new Date(route.query.date);
      if (!isNaN(dateObj.getTime())) {
        filters.dateFilter = dateObj;
      }
    }
  }

  return {
    // State
    layoutMode,
    authorUuid,
    searchText,
    dateFilter,

    // Computed
    activeGroupId,

    // Getters
    hasActiveFilters,
    getFilterParams,

    // Actions
    setLayoutMode,
    setAuthorFilter,
    setSearchText,
    setDateFilter,
    clearAllFilters,
    loadFiltersFromUrl,
  };
});
